import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Brain, Code, Cpu, Shield, Zap, Globe, MessageSquare, BarChart3 } from 'lucide-react';
import Navbar from './Navbar';

const AIToolsetDevelopment = () => {
  const navigate = useNavigate();
  const [showStickyButton, setShowStickyButton] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Show sticky button when user scrolls past 50% of the page
      const scrollPercentage = scrollPosition / (documentHeight - windowHeight);
      setShowStickyButton(scrollPercentage > 0.5);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToContact = () => {
    navigate('/?subject=AI%20Development%20Inquiry', { replace: true });
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const aiBenefits = [
    {
      icon: Brain,
      title: 'Intelligent Automation',
      description: 'Automate complex business processes with AI-powered decision making'
    },
    {
      icon: MessageSquare,
      title: 'AI Model Integration',
      description: 'Seamlessly integrate with OpenAI, Claude, Gemini, and other leading models'
    },
    {
      icon: BarChart3,
      title: 'Data-Driven Insights',
      description: 'Extract actionable insights from your business data using advanced AI'
    },
    {
      icon: Code,
      title: 'Custom Development',
      description: 'Tailored AI solutions built specifically for your business requirements'
    },
    {
      icon: Shield,
      title: 'Secure & Compliant',
      description: 'Enterprise-grade security with data privacy and compliance standards'
    },
    {
      icon: Zap,
      title: 'Rapid Deployment',
      description: 'Fast implementation with minimal disruption to existing workflows'
    }
  ];

  const aiCapabilities = [
    {
      category: 'Natural Language Processing',
      features: [
        'Document analysis and summarization',
        'Automated content generation',
        'Multi-language translation',
        'Sentiment analysis and classification'
      ]
    },
    {
      category: 'Business Process Automation',
      features: [
        'Workflow optimization',
        'Decision support systems',
        'Predictive analytics',
        'Quality assurance automation'
      ]
    },
    {
      category: 'Integration & Deployment',
      features: [
        'API development and integration',
        'Cloud-native architecture',
        'Real-time monitoring and alerts',
        'Scalable infrastructure setup'
      ]
    }
  ];

  return (
    <>
      <Navbar />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-primary-50 to-white pt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="max-w-4xl">
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8 transition-colors duration-200"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Home
              </button>
              
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                AI Toolset Development
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                We build custom tools that work with an array of existing AI models to integrate AI into your business processes. 
                From intelligent automation to data analysis, our AI solutions are designed to enhance your operational efficiency 
                and drive measurable business results.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <button
                  onClick={scrollToContact}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Start Your AI Journey
                </button>
                <button
                  onClick={() => {
                    const element = document.getElementById('capabilities');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="border-2 border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200"
                >
                  Explore Capabilities
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Transform Your Business with AI
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our AI toolset development services help you harness the power of artificial intelligence 
                to streamline operations, enhance decision-making, and unlock new opportunities.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {aiBenefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <div key={index} className="bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-shadow duration-300">
                    <div className="flex items-center justify-center w-12 h-12 bg-primary-100 rounded-lg mb-4">
                      <IconComponent className="h-6 w-6 text-primary-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* AI Capabilities Section */}
        <div id="capabilities" className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Our AI Development Capabilities
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We specialize in developing comprehensive AI solutions across multiple domains, 
                ensuring your business stays ahead of the technology curve.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {aiCapabilities.map((capability, index) => (
                <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    {capability.category}
                  </h3>
                  <ul className="space-y-4">
                    {capability.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Process Section */}
        <div className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Our Development Process
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                From discovery to deployment, we follow a proven methodology to ensure your AI solution 
                delivers maximum value and seamlessly integrates with your existing systems.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  step: '01',
                  title: 'Discovery & Analysis',
                  description: 'We analyze your business processes and identify AI opportunities'
                },
                {
                  step: '02',
                  title: 'Solution Design',
                  description: 'Custom AI architecture tailored to your specific requirements'
                },
                {
                  step: '03',
                  title: 'Development & Testing',
                  description: 'Agile development with continuous testing and validation'
                },
                {
                  step: '04',
                  title: 'Deployment & Support',
                  description: 'Seamless deployment with ongoing monitoring and optimization'
                }
              ].map((process, index) => (
                <div key={index} className="text-center">
                  <div className="bg-primary-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    {process.step}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {process.title}
                  </h3>
                  <p className="text-gray-600">
                    {process.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-20 bg-primary-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Ready to Harness the Power of AI?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Every busness is different. Let's discuss how our AI toolset development services can transform your operations 
              and unlock new possibilities for growth and efficiency.
            </p>
            <button
              onClick={scrollToContact}
              className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Get Started with AI Development
            </button>
          </div>
        </div>

        {/* Sticky CTA Button */}
        {showStickyButton && (
          <button
            onClick={scrollToContact}
            className="fixed bottom-6 right-6 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-200 z-50"
          >
            Contact Us
          </button>
        )}
      </div>
    </>
  );
};

export default AIToolsetDevelopment;